import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Dialog } from "component/shared/dialog";
import { Select } from "component/shared/select";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { deleteCampaignDisplayCategory } from "redux/slice/campaign-display-categories";
import { getSortedCampaignDisplayCategories } from "redux/selector";
import {
	CampaignCategoryGroup,
	categorySelectOptions,
	categoryValueToFk,
	findDefaultCategory,
	UNCATEGORIZED_ID,
} from "shared/campaign-grouping";
import { styles } from "./style";

interface OwnProps {
	// The category to delete (real category only). `undefined` keeps the dialog closed.
	group?: CampaignCategoryGroup;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * MAX-3878 — Delete Category flows. Two variants, both titled "Delete this category?" (figma
 * 1009:10545 empty, 1008:11130 move-&-delete). The last-remaining-real-category case never reaches
 * this dialog — the menu item is disabled with a tooltip (client guard; server also 409s).
 *
 * - Empty (no campaigns): body "This cannot be undone." / "Cancel" · "Delete Category".
 * - Move & Delete: body "This category contains N campaigns. Select a category to move them to
 *   before deleting." + destination dropdown "Move campaigns to" (default General) /
 *   "Cancel" · "Move & Delete Category". One atomic server op (AR4); moveToId null ⇒ Uncategorized.
 */
const Component: React.FC<Props> = ({ group, onClose, classes = {} }) => {
	const categories = useSelector(getSortedCampaignDisplayCategories);
	const dispatch = useAppThunkDispatch();
	const open = !!group;
	const hasCampaigns = !!group && group.count > 0;

	// Destinations: every real category except the one being deleted, plus Uncategorized.
	const destinationOptions = categorySelectOptions(categories, {
		includeUncategorized: true,
		excludeId: group?.id,
	});

	const defaultDestinationId =
		findDefaultCategory(categories.filter((c) => c.id !== group?.id))?.id ??
		destinationOptions[0]?.id ??
		UNCATEGORIZED_ID;

	const [destination, setDestination] = useState<number>(defaultDestinationId);

	// Reset the destination to the default (General) each time the dialog opens for a category.
	useEffect(() => {
		if (group) {
			setDestination(defaultDestinationId);
		}
	}, [group]);

	const handleDelete = () => {
		if (!group) {
			return;
		}
		if (hasCampaigns) {
			dispatch(deleteCampaignDisplayCategory({ id: group.id, moveToId: categoryValueToFk(destination) }));
		} else {
			dispatch(deleteCampaignDisplayCategory({ id: group.id }));
		}
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<Mui.DialogTitle>Delete this category?</Mui.DialogTitle>
			<Mui.DialogContent>
				{hasCampaigns ? (
					<>
						<Mui.Typography className={classes.body}>
							This category contains {group?.count} campaigns. Select a category to move them
							to before deleting.
						</Mui.Typography>
						<Select
							variant="outlined"
							margin="dense"
							label="Move campaigns to"
							required
							fullWidth
							value={destination}
							options={destinationOptions}
							valueExtractor={(c) => c.id}
							labelExtractor={(c) => c.name}
							optionRenderer={(c) => <Mui.ListItemText primary={c.name} />}
							onChange={(value) => setDestination(value)}
						/>
					</>
				) : (
					<Mui.Typography>This cannot be undone.</Mui.Typography>
				)}
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions}>
				<Mui.Button variant="outlined" color="primary" onClick={onClose}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" className={classes.destructive} onClick={handleDelete}>
					{hasCampaigns ? "Move & Delete Category" : "Delete Category"}
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const DeleteCategoryDialog = withStyles(Component, styles);
